// Site designs: whole-page templates an agency chooses between. A design owns
// the public site chrome (navbar + footer) and the composition of the home
// page. Each design declares the sections it can render, so two agencies on
// different designs never end up looking like the same website.
//
// The chosen design is stored inside the storefront appearance, which means it
// flows through the same draft -> publish -> revert workflow as the rest of the
// site copy.

import type { CornerStyle, FontId, HeroLayout, SiteAppearance } from '@/lib/storefront/appearance';

export type DesignId = 'classic' | 'editorial' | 'roadtrip';

/** Whether a section belongs on the home page or is a site-wide feature. */
export type SectionScope = 'home' | 'site';

export interface SectionDefinition {
  id: string;
  label: string;
  hint: string;
  scope: SectionScope;
  /** Used when the agency has not made a choice yet (and on design switch). */
  defaultEnabled: boolean;
}

export interface DesignPreset {
  primaryColor: string;
  secondaryColor: string;
  font: FontId;
  corners: CornerStyle;
  heroLayout: HeroLayout;
}

export interface SiteDesign {
  id: DesignId;
  label: string;
  tagline: string;
  description: string;
  /**
   * Tailwind classes for the site root. The design owns its own surface
   * treatment (the classic one is white, the editorial one cream) while the
   * accent colour still comes from the agency palette.
   */
  canvas: string;
  /** Which of the shared appearance options this design actually honours. */
  options: { heroLayout: boolean };
  /** Palette + typography applied when the agency picks this design. */
  preset: DesignPreset;
  /** Ordered: the home page renders the enabled `home` entries in this order. */
  sections: SectionDefinition[];
}

/**
 * Section catalogue. Designs pick from it, so a toggle keeps the same meaning
 * across designs (the FAQ is one switch even though the classic design shows it
 * on the contact page and the road trip design shows it on the home page).
 */
const SECTIONS = {
  search: {
    id: 'search',
    label: 'Search bar',
    hint: 'Vehicle search form under the hero.',
    scope: 'home',
    defaultEnabled: true,
  },
  stats: {
    id: 'stats',
    label: 'Why choose us',
    hint: 'Statistics strip on the home page.',
    scope: 'home',
    defaultEnabled: true,
  },
  inventory: {
    id: 'inventory',
    label: 'Vehicle showcase',
    hint: 'Featured vehicles from your fleet.',
    scope: 'home',
    defaultEnabled: true,
  },
  promo: {
    id: 'promo',
    label: 'Promotion banner',
    hint: 'Discount call to action.',
    scope: 'home',
    defaultEnabled: true,
  },
  whyChooseUs: {
    id: 'whyChooseUs',
    label: 'Why choose us',
    hint: 'Photo next to your selling points.',
    scope: 'home',
    defaultEnabled: true,
  },
  numbers: {
    id: 'numbers',
    label: 'Our numbers',
    hint: 'Fleet size, categories and pick-up points.',
    scope: 'home',
    defaultEnabled: true,
  },
  fleetCategories: {
    id: 'fleetCategories',
    label: 'Fleet categories',
    hint: 'Your categories with a price from your fleet.',
    scope: 'home',
    defaultEnabled: true,
  },
  safetyStrip: {
    id: 'safetyStrip',
    label: 'Safety & comfort',
    hint: 'Short feature strip under the hero.',
    scope: 'home',
    defaultEnabled: true,
  },
  packages: {
    id: 'packages',
    label: 'Travel packages',
    hint: 'Multi-day offers you add in the Website tab.',
    scope: 'home',
    defaultEnabled: true,
  },
  appBand: {
    id: 'appBand',
    label: 'App band',
    hint: 'Dark band with your phone number.',
    scope: 'home',
    defaultEnabled: true,
  },
  testimonials: {
    id: 'testimonials',
    label: 'Testimonials',
    hint: 'Customer quotes (add them in the Website tab).',
    scope: 'home',
    defaultEnabled: true,
  },
  newsletter: {
    id: 'newsletter',
    label: 'Newsletter',
    hint: 'Email signup block.',
    scope: 'home',
    defaultEnabled: true,
  },
  deals: {
    id: 'deals',
    label: 'Deals page',
    hint: 'Special offers page and its navigation entry.',
    scope: 'site',
    defaultEnabled: true,
  },
  faq: {
    id: 'faq',
    label: 'FAQ',
    hint: 'Frequently asked questions.',
    scope: 'site',
    defaultEnabled: true,
  },
} satisfies Record<string, SectionDefinition>;

export const SITE_DESIGNS: SiteDesign[] = [
  {
    id: 'classic',
    label: 'Classic',
    tagline: 'Cinematic dark',
    description: 'Full-width night-time hero, search bar, selling points, vehicle grid and a promo banner.',
    canvas: 'bg-white text-slate-900',
    options: { heroLayout: true },
    preset: {
      primaryColor: '#FF8A00',
      secondaryColor: '#0d1117',
      font: 'system',
      corners: 'rounded',
      heroLayout: 'full-bleed',
    },
    sections: [
      SECTIONS.search,
      SECTIONS.stats,
      SECTIONS.inventory,
      SECTIONS.promo,
      SECTIONS.testimonials,
      SECTIONS.newsletter,
      SECTIONS.deals,
      SECTIONS.faq,
    ],
  },
  {
    id: 'editorial',
    label: 'Editorial',
    tagline: 'Bright & minimal',
    description: 'Cream layout with a two-tone headline, a search card in the hero, an inventory grid and a why-choose-us block.',
    canvas: 'bg-[#FAF6F0] text-slate-900',
    options: { heroLayout: false },
    preset: {
      primaryColor: '#FF8A00',
      secondaryColor: '#111827',
      font: 'manrope',
      corners: 'soft',
      heroLayout: 'centered',
    },
    sections: [
      { ...SECTIONS.inventory, label: 'Latest inventory' },
      SECTIONS.whyChooseUs,
      SECTIONS.numbers,
      SECTIONS.testimonials,
      SECTIONS.newsletter,
      SECTIONS.deals,
      SECTIONS.faq,
    ],
  },
  {
    id: 'roadtrip',
    label: 'Road trip',
    tagline: 'Travel & outdoors',
    description: 'Photo hero with a full booking widget, fleet categories, safety strip, travel packages, FAQ and an app band.',
    canvas: 'bg-white text-slate-900',
    options: { heroLayout: false },
    preset: {
      primaryColor: '#F97316',
      secondaryColor: '#0F172A',
      font: 'poppins',
      corners: 'rounded',
      heroLayout: 'full-bleed',
    },
    sections: [
      SECTIONS.fleetCategories,
      SECTIONS.safetyStrip,
      SECTIONS.packages,
      SECTIONS.testimonials,
      { ...SECTIONS.faq, scope: 'home' },
      SECTIONS.appBand,
      SECTIONS.newsletter,
      SECTIONS.deals,
    ],
  },
];

/** The design list as an enum tuple for the appearance schema. */
export const DESIGN_IDS = SITE_DESIGNS.map((design) => design.id) as [
  DesignId,
  ...DesignId[],
];

/** Resolves an unknown id (or a document from before this feature) safely. */
export function designById(id: string | null | undefined): SiteDesign {
  return SITE_DESIGNS.find((design) => design.id === id) ?? SITE_DESIGNS[0];
}

/** The sections a design can render, optionally narrowed to one scope. */
export function designSections(design: SiteDesign, scope?: SectionScope): SectionDefinition[] {
  return scope ? design.sections.filter((section) => section.scope === scope) : design.sections;
}

/** Every switch this design knows about, at its default. */
export function defaultSections(design: SiteDesign): Record<string, boolean> {
  const sections: Record<string, boolean> = {};
  for (const section of design.sections) {
    sections[section.id] = section.defaultEnabled;
  }
  return sections;
}

/**
 * Fills in the switches this design owns. Keys kept from another design are
 * dropped, so a switch that no longer exists can never disable something.
 */
export function mergeSections(
  design: SiteDesign,
  current: Record<string, boolean> | undefined,
): Record<string, boolean> {
  const sections = defaultSections(design);
  if (!current) {
    return sections;
  }
  for (const [key, value] of Object.entries(current)) {
    if (typeof value === 'boolean' && key in sections) {
      sections[key] = value;
    }
  }
  return sections;
}

/** Whether a design renders a section (unknown ids are off). */
export function sectionEnabled(appearance: SiteAppearance, id: string): boolean {
  return appearance.sections[id] === true;
}

/**
 * Switching design applies the design's recommended palette and typography and
 * adopts its section list, while keeping switches the two designs share. Every
 * value stays editable afterwards.
 */
export function applyDesignPreset(design: SiteDesign, appearance: SiteAppearance): SiteAppearance {
  return {
    ...appearance,
    design: design.id,
    ...design.preset,
    sections: mergeSections(design, appearance.sections),
  };
}
